/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Box, Grid, Stack } from "@mui/material";
import { Method } from "axios";
import { H4 } from "components/generals/_Titles";
import FormikForm from "components/layout/FormikForm";
import BadgeIcon from "@mui/icons-material/Badge";
import { CoreAPI_URLs } from "constants/http.const";
import { Form, FormikProps } from "formik";
import useHttp from "hooks/http.hook";
import { HeaderEnum, PharmacyInterface, UserRolesEnum } from "@malmur-technologies/medicalsystem-shared";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import { FormProps } from "interfaces";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import FormField from "components/generals/FormInput";

import Button from "components/generals/Button";
import FormActionButtons from "./custom/FormActionButtons";
import FormSwitch from "components/generals/Switch";
import { pharmacyFormYUPValidation, validatePhoneInputSymbol } from "utils/form.utils";
import FormPhoneInput from "components/generals/FormPhoneInput";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { selectIsPatientOrDoctor } from "store/slices/auth.slice";

/**
 * Pharmacy form component for managing pharmacy information in the system.
 * 
 * Features:
 * - Create/edit pharmacy records with complete details
 * - Pharmacy name and identification
 * - Complete address information (country, state, city, ZIP, street address)
 * - Contact information (phone, fax, email)
 * - Active/inactive status toggle
 * - Phone/fax number validation and formatting
 * - Permission-based access control (read-only for patients/doctors)
 * - Form validation with Yup schema
 * - Success messages on create/update
 * - Automatic list update after save
 * 
 * Use Cases:
 * - Managing pharmacy network for prescription fulfillment
 * - Maintaining pharmacy contact information
 * - Associating pharmacies with patients
 * - E-prescribing integration
 * 
 * Security:
 * - Permission-based rendering via PermissionWrapper
 * - Read-only mode for patients and doctors
 * - Validates entity permissions for edit/delete
 * - Uses HeaderEnum.RESOURCE_ID for secure identification
 * - Phone/fax sanitization to prevent injection
 * 
 * @param {FormProps<PharmacyInterface>} props - Form properties
 * @param {PharmacyInterface} props.model - Pharmacy data to edit or empty model for new
 * @param {FormActionType} props.action - Form action (NEW, MODIFY, READ_ONLY)
 * @param {Function} props.onCloseAction - Callback when form closes
 * @param {Function} props.setModelList - Callback to update pharmacy list
 * @param {PharmacyInterface[]} props.modelList - Current list of pharmacies
 * 
 * @returns {JSX.Element} Pharmacy form with contact and address fields
 */
const PharmacyForm: React.FC<FormProps<PharmacyInterface>> = ({
  model,
  action,
  onCloseAction,
  setModelList,
  modelList,
}) => {
  const [isLoading, _error, sendRequest] = useHttp();
  const { t } = useTranslation();

  const isReadonly = useSelector(selectIsPatientOrDoctor);

  const submitHandler = (model: Partial<PharmacyInterface>) => {
    if (isReadonly) {
      return;
    }

    const submitModel = { ...model };

    if (submitModel.phone) {
      submitModel.phone = validatePhoneInputSymbol(submitModel.phone);
    }
    if (submitModel.fax) {
      submitModel.fax = validatePhoneInputSymbol(submitModel.fax);
    }

    const method: Method = action === FormActionType.NEW ? "POST" : "PUT";
    const successMessageText = action === FormActionType.NEW ? t("pharmacyForm.pharmacyCreated") : t("pharmacyForm.pharmacyUpdated");
    sendRequest(
      CoreAPI_URLs.PHARMACIES_GENERAL_OPERATION,
      onPharmacyUpdateHandler,
      method,
      JSON.stringify(submitModel),
      [{ header: HeaderEnum.RESOURCE_ID, value: submitModel.id }],
      { successMessage: successMessageText }
    );
  };

  const onPharmacyUpdateHandler = (pharmacy: PharmacyInterface) => {
    if (action === FormActionType.MODIFY) {
      setModelList(modelList.map((model) => (model.id === pharmacy.id ? pharmacy : model)));
    } else {
      setModelList((oldArray) => [...oldArray, pharmacy]);
    }

    onCloseAction();
  };

  return (
    <Box sx={{ maxWidth: '80%', mx: 'auto', px: 2 }}>
      <FormikForm
        onSubmit={(model) => {
          submitHandler(model);
        }}
        initialValues={model}
        validationSchema={pharmacyFormYUPValidation(t)}
      >
        {({ values, errors, touched, setFieldValue }: FormikProps<Partial<PharmacyInterface>>) => (
          <Form id="pharmacyForm" autoComplete="true">
            <Stack alignItems="center" direction="row" gap={2} mb={3}>
              <BadgeIcon fontSize="large" />
              <H4>{t("pharmacyForm.pharmacyFormTitle")}</H4>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={6}>
                <FormField
                  type="text"
                  label={t("pharmacyForm.displayName")}
                  name="displayName"
                  value={values.displayName || ""}
                  error={errors.displayName && touched.displayName}
                  disabled={isReadonly}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <FormField
                  type="text"
                  label={t("pharmacyForm.email")}
                  name="email"
                  value={values.email || ""}
                  error={errors.email && touched.email}
                  disabled={isReadonly}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <FormField
                  type="text"
                  label={t("pharmacyForm.name")}
                  name="storeName"
                  value={values.storeName || ""}
                  error={errors.storeName && touched.storeName}
                  disabled={isReadonly}
                />
              </Grid>
              {!isReadonly && (
                <Grid item xs={12} md={6} lg={6}>
                  <FormField
                    type="text"
                    label={t("pharmacyForm.ncpdpid")}
                    name="ncpdID"
                    value={values.ncpdID || ""}
                    error={errors.ncpdID && touched.ncpdID}
                  />
                </Grid>
              )}
              {!isReadonly && (
                <Grid item xs={12} md={6} lg={6}>
                  <FormSwitch
                    label={t("pharmacyForm.eprescribe")}
                    value={values.ePrescribe}
                    name="ePrescribe"
                    setFieldValue={setFieldValue}
                  />
                </Grid>
              )}
              {!isReadonly && (
                <Grid item xs={12} md={6} lg={6} mb={2}>
                  <FormSwitch
                    label={t("pharmacyForm.mailOrder")}
                    value={values.mailOrderPharmacy}
                    name="mailOrderPharmacy"
                    setFieldValue={setFieldValue}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormField
                  type="text"
                  label={t("pharmacyForm.address")}
                  name="address"
                  value={values.address || ""}
                  error={errors.address && touched.address}
                  disabled={isReadonly}
                />
              </Grid>
              <Grid item xs={12}>
                <FormField
                  type="text"
                  label={t("pharmacyForm.address2")}
                  name="address2"
                  value={values.address2 || ""}
                  error={errors.address2 && touched.address2}
                  disabled={isReadonly}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} mb={4}>
              <Grid item xs={12} md={3} lg={3}>
                <FormField
                  type="text"
                  label={t("pharmacyForm.city")}
                  name="city"
                  value={values.city || ""}
                  error={errors.city && touched.city}
                  disabled={isReadonly}
                />
              </Grid>
              <Grid item xs={12} md={3} lg={3}>
                <FormField
                  type="text"
                  label={t("pharmacyForm.state")}
                  name="state"
                  value={values.state || ""}
                  error={errors.state && touched.state}
                  disabled={isReadonly}
                />
              </Grid>
              <Grid item xs={12} md={3} lg={3}>
                <FormField
                  type="text"
                  label={t("pharmacyForm.county")}
                  name="county"
                  value={values.county || ""}
                  error={errors.county && touched.county}
                  disabled={isReadonly}
                />
              </Grid>
              <Grid item xs={12} md={1.5} lg={1.5}>
                <FormField
                  type="text"
                  label={t("pharmacyForm.zip")}
                  name="zip"
                  value={values.zip || ""}
                  error={errors.zip && touched.zip}
                  disabled={isReadonly}
                />
              </Grid>
              <Grid item xs={12} md={1.5} lg={1.5}>
                <FormField
                  type="number"
                  label={t("pharmacyForm.ext")}
                  name="ext"
                  value={values.ext || ""}
                  error={errors.ext && touched.ext}
                  disabled={isReadonly}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={12} lg={4.5}>
                <Grid item xs={12}>
                  {isReadonly ? (
                    <FormField
                      type="text"
                      label={t("pharmacyForm.phone")}
                      name="phone"
                      value={values.phone || ""}
                      error={errors.phone && touched.phone}
                      disabled={true}
                    />
                  ) : (
                    <FormPhoneInput
                      name="phone"
                      specialLabel={t("pharmacyForm.phone")}
                      country={"us"}
                      required={true}
                      value={values.phone || ""}
                      error={errors.phone && touched.phone}
                      onChange={(value) => {
                        setFieldValue("phone", value);
                      }}
                    />
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12} md={12} lg={4.5}>
                {isReadonly ? (
                  <FormField
                    type="text"
                    label={t("pharmacyForm.fax")}
                    name="fax"
                    value={values.fax || ""}
                    error={errors.fax && touched.fax}
                    disabled={true}
                  />
                ) : (
                  <FormPhoneInput
                    name="fax"
                    specialLabel={t("pharmacyForm.fax")}
                    required={false}
                    country={"us"}
                    value={values.fax || ""}
                    error={errors.fax && touched.fax}
                    onChange={(value) => {
                      setFieldValue("fax", value);
                    }}
                  />
                )}
              </Grid>
            </Grid>
            <Grid container spacing={2} className="form-container-action-buttons">
              <PermissionWrapper
                requiredPermissions={[PermissionsUser[EntityPermissions.PHARMACY].EDIT]}
              >
                <Grid item xs={12} sm="auto" md="auto">
                  {action === FormActionType.NEW ? (
                    <PermissionWrapper
                      requiredPermissions={[PermissionsUser[EntityPermissions.PHARMACY].CREATE]}
                    >
                      <Button type="submit" variant="contained" color="info" disabled={isLoading}>
                        {t("stepperFormActionButtons.submit")}
                      </Button>
                    </PermissionWrapper>
                  ) : (
                    <PermissionWrapper
                      requiredPermissions={[PermissionsUser[EntityPermissions.PHARMACY].EDIT]}
                    >
                      <Button type="submit" variant="contained" color="info" disabled={isLoading}>
                        {t("stepperFormActionButtons.update")}
                      </Button>
                    </PermissionWrapper>
                  )}
                </Grid>
              </PermissionWrapper>
              <Grid item xs={12} sm="auto" md="auto">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={onCloseAction}
                >
                  {t("common.close")}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </FormikForm>
    </Box>
  );
};
export default PharmacyForm;